import { inject, computed, type ComputedRef, ref } from 'vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { replaceThemePresetsWithInlineStyles } from '@canvas-builder/util/print.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

// Global print mode section counters per sectionListId
const printModeSectionCounters = ref<Record<string, number>>({})
const nextSectionIndex = ref(0)

export default function usePrintMode() {
  const { isPrintDisplayMode } = useCanvas()
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  // Get the next sequential index for print mode
  const getNextPrintModeIndex = (sectionListId: string): number => {
    if (!isPrintDisplayMode.value) return 0

    // Initialize counter for this sectionListId if not exists
    if (!(sectionListId in printModeSectionCounters.value)) {
      printModeSectionCounters.value[sectionListId] = 0
    }

    const currentIndex = printModeSectionCounters.value[sectionListId]
    printModeSectionCounters.value[sectionListId]++
    return currentIndex
  }

  // Reset counters (useful when print mode is exited/entered)
  const resetPrintModeCounters = () => {
    printModeSectionCounters.value = {}
    nextSectionIndex.value = 0
  }

  const processPrintContent = (content: string): string => {
    if (!content) return content

    if (isPrintDisplayMode.value) {
      return replaceThemePresetsWithInlineStyles(content, !!launchDarkly.value.enable_scale_content)
    }

    return content
  }

  // Simplified function that applies print mode transformation if needed
  const applyPrintModeToElement = (element: HTMLElement | null, getContent: () => string): void => {
    if (!element || !isPrintDisplayMode.value) return

    // Only apply transformation if in print mode
    const currentContent = getContent()
    const processedContent = replaceThemePresetsWithInlineStyles(
      currentContent,
      !!launchDarkly.value.enable_scale_content,
    )
    element.innerHTML = processedContent

    // Process any remaining CSS variables directly in the DOM
    resolveAllCssVariables(element)
  }

  /**
   * Resolves all CSS variables in the element and its children to their computed values
   * @param element The root element to process
   */
  const resolveAllCssVariables = (element: HTMLElement): void => {
    if (!element || !isPrintDisplayMode.value) return

    // For elements that are inside sections with processed themes, we need to be careful
    // not to override any section-specific styles that were directly applied
    const allElements = [element, ...Array.from(element.querySelectorAll('*'))] as HTMLElement[]

    allElements.forEach((el) => {
      // Skip elements that are already fully processed with direct styles
      if (
        el.hasAttribute('data-theme-h1-applied') ||
        el.hasAttribute('data-theme-h2-applied') ||
        el.hasAttribute('data-theme-h3-applied') ||
        el.hasAttribute('data-theme-big_text-applied') ||
        el.hasAttribute('data-theme-normal_text-applied') ||
        el.hasAttribute('data-theme-small_text-applied')
      ) {
        return
      }

      const computedStyle = getComputedStyle(el)

      const criticalProperties = [
        'color',
        'font-family',
        'font-size',
        'font-weight',
        'letter-spacing',
        'line-height',
        'text-transform',
      ]

      // Handle inline style properties with CSS variables
      for (let i = 0; i < el.style.length; i++) {
        const prop = el.style[i]
        const value = el.style.getPropertyValue(prop)

        if (value.includes('var(--')) {
          const computedValue = computedStyle.getPropertyValue(prop)
          if (computedValue && computedValue !== value) {
            el.style.setProperty(prop, computedValue)
          }
        }
      }

      // Apply critical properties with direct property assignment
      criticalProperties.forEach((prop) => {
        const computedValue = computedStyle.getPropertyValue(prop)
        const currentValue = el.style.getPropertyValue(prop)

        // Only apply if not already set, or if using CSS variables
        if ((!currentValue || currentValue.includes('var(--')) && computedValue) {
          const camelCaseProp = prop.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())

          // Skip default values for certain properties
          if (
            (prop === 'letter-spacing' && computedValue === 'normal') ||
            (prop === 'text-transform' && computedValue === 'none')
          ) {
            return
          }

          // Use property assignment for better performance and specificity
          el.style[camelCaseProp as any] = computedValue
        }
      })

      // Resolve CSS variables in the style attribute
      if (el.hasAttribute('style')) {
        let styleAttr = el.getAttribute('style') || ''

        if (styleAttr.includes('var(--')) {
          const varMatches = styleAttr.match(/var\(--[^)]+\)/g) || []

          for (const varMatch of varMatches) {
            const varName = varMatch.substring(4, varMatch.length - 1)
            const varValue = computedStyle.getPropertyValue(varName).trim()

            if (varValue) {
              styleAttr = styleAttr.replace(varMatch, varValue)
            }
          }

          el.setAttribute('style', styleAttr)
        }
      }
    })
  }

  const applyTypographyStyles = (element: HTMLElement, typographyValue: any, enableExperiments: boolean): void => {
    if (enableExperiments) {
      // Apply styles only if not already set
      if (typographyValue.font_family && !element.style.fontFamily)
        element.style.fontFamily = typographyValue.font_family
      if (typographyValue.font_size && !element.style.fontSize) element.style.fontSize = typographyValue.font_size
      if (typographyValue.font_weight && !element.style.fontWeight)
        element.style.fontWeight = typographyValue.font_weight
      if (typographyValue.line_height && !element.style.lineHeight)
        element.style.lineHeight = typographyValue.line_height
      if (
        typographyValue.letter_spacing &&
        typographyValue.letter_spacing !== 'normal' &&
        !element.style.letterSpacing
      ) {
        element.style.letterSpacing = typographyValue.letter_spacing
      }
      if (typographyValue.text_transform && typographyValue.text_transform !== 'none' && !element.style.textTransform) {
        element.style.textTransform = typographyValue.text_transform
      }
      if (typographyValue.color && !element.style.color) {
        element.style.color = typographyValue.color
      }
    } else {
      // Original behavior - always apply if value exists
      if (typographyValue.font_family) element.style.fontFamily = typographyValue.font_family
      if (typographyValue.font_size) element.style.fontSize = typographyValue.font_size
      if (typographyValue.font_weight) element.style.fontWeight = typographyValue.font_weight
      if (typographyValue.line_height) element.style.lineHeight = typographyValue.line_height
      if (typographyValue.letter_spacing && typographyValue.letter_spacing !== 'normal') {
        element.style.letterSpacing = typographyValue.letter_spacing
      }
      if (typographyValue.text_transform && typographyValue.text_transform !== 'none') {
        element.style.textTransform = typographyValue.text_transform
      }
      if (typographyValue.color) element.style.color = typographyValue.color
    }
  }

  /**
   * Applies print mode styling to a section element by directly rasterizing all CSS variables
   * and theme styles to ensure proper printing
   *
   * @param section The section data to process
   * @param sectionElement The DOM element of the section to apply styles to
   */
  const applyPrintModeToSection = (
    section: { id: string; theme?: any },
    sectionElement: HTMLElement,
    maintainTheme = true,
  ): void => {
    if (!sectionElement || !isPrintDisplayMode.value || !maintainTheme) return

    // Skip theme application if the section inherits theme from parent
    const shouldInheritFromParent = section.theme?.inherit_theme_from_parent === true
    const enableScaleContent = !!launchDarkly.value.enable_scale_content

    // Skip if this section was already processed
    if (sectionElement.hasAttribute('data-theme-processed')) return

    // Mark the element with a unique identifier for processing
    sectionElement.setAttribute('data-section-id-debug', section.id)

    // Apply section-specific theme if available and not inheriting from parent
    if (section.theme && !shouldInheritFromParent) {
      // 1. Apply background and section-level styles
      if (section.theme.canvas_bg_color) {
        sectionElement.style.backgroundColor = section.theme.canvas_bg_color
      }

      if (section.theme.style) {
        Object.entries(section.theme.style).forEach(([prop, value]) => {
          if (value && typeof value === 'string') {
            const camelProp = prop.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
            sectionElement.style[camelProp as any] = value
          }
        })
      }

      // 2. Process typography settings and apply to elements
      if (section.theme.typography) {
        const typography = section.theme.typography

        // Set section data attribute to mark it as processed
        sectionElement.setAttribute('data-theme-processed', 'true')

        // Find all text elements in the section that match our typography targets
        const elements = {
          h1: Array.from(sectionElement.querySelectorAll('h1, .c-theme-preset--h_1')) as HTMLElement[],
          h2: Array.from(sectionElement.querySelectorAll('h2, .c-theme-preset--h_2')) as HTMLElement[],
          h3: Array.from(sectionElement.querySelectorAll('h3, .c-theme-preset--h_3')) as HTMLElement[],
          big_text: Array.from(
            sectionElement.querySelectorAll('.c-theme-preset--big_text, p.big, p.big_text'),
          ) as HTMLElement[],
          normal_text: Array.from(
            sectionElement.querySelectorAll(
              '.c-theme-preset--normal_text, p:not(.big):not(.small):not(.big_text):not(.small_text)',
            ),
          ) as HTMLElement[],
          small_text: Array.from(
            sectionElement.querySelectorAll('.c-theme-preset--small_text, p.small, p.small_text'),
          ) as HTMLElement[],
          c_text: Array.from(sectionElement.querySelectorAll('.canvas-text')) as HTMLElement[],
        }

        // Additional selectors for paragraph texts - text components are often wrapped differently
        const paragraphContainers = Array.from(
          sectionElement.querySelectorAll('.canvas-text, .c-text__rich-text-editor, .c-text__content'),
        ) as HTMLElement[]

        // Process each typography preset in the theme
        Object.entries(typography).forEach(([key, value]) => {
          if (!value) return

          const typographyValue = value as any
          // Replace h_1 with h1 for element mapping
          const elementKey = key.replace('h_', 'h') as keyof typeof elements
          const targetElements = elements[elementKey]

          if (targetElements?.length) {
            // Apply styles directly to matching elements
            targetElements.forEach((element) => {
              // Remove any theme-related classes to prevent style inheritance from global theme
              element.classList.remove('c-theme-preset', `c-theme-preset--${key}`)

              // Store the original value on the element for processing tracking
              element.setAttribute(`data-theme-${key}-applied`, 'true')
              element.setAttribute('data-section-styled', section.id)

              // Apply direct styles to ensure they override any CSS variables
              applyTypographyStyles(element, typographyValue, enableScaleContent)

              // Process all nested spans inside this element
              const spans = element.querySelectorAll('span')
              if (spans.length > 0) {
                spans.forEach((span) => {
                  const spanEl = span as HTMLElement
                  spanEl.setAttribute('data-section-styled', section.id)

                  applyTypographyStyles(spanEl, typographyValue, enableScaleContent)
                })
              }
            })
          }

          // Special handling for paragraph styles in the canvas-text containers
          if (key.includes('_text') && paragraphContainers.length > 0) {
            // Figure out which paragraph selector to use based on the key
            let paragraphSelector = 'p'
            if (key === 'big_text') paragraphSelector = 'p.big, p.big_text'
            else if (key === 'normal_text') paragraphSelector = 'p:not(.big):not(.small)'
            else if (key === 'small_text') paragraphSelector = 'p.small, p.small_text'

            // Apply to paragraphs inside the text containers
            paragraphContainers.forEach((container) => {
              const paragraphs = container.querySelectorAll(paragraphSelector)
              paragraphs.forEach((p) => {
                const pElement = p as HTMLElement

                // Skip if already processed
                if (pElement.hasAttribute(`data-theme-${key}-applied`)) return

                pElement.setAttribute(`data-theme-${key}-applied`, 'true')
                pElement.setAttribute('data-section-styled', section.id)

                applyTypographyStyles(pElement, typographyValue, enableScaleContent)

                // Also style spans inside the paragraph
                pElement.querySelectorAll('span').forEach((span) => {
                  const spanEl = span as HTMLElement
                  spanEl.setAttribute('data-section-styled', section.id)

                  applyTypographyStyles(spanEl, typographyValue, enableScaleContent)
                })
              })
            })
          }
        })
      }
    } else {
      // Mark the section as inheriting from parent
      sectionElement.setAttribute('data-theme-inherited', 'true')
    }

    // Force computation of critical styles before resolving variables
    // This step is important for both section-specific themes and inherited themes
    const style = window.getComputedStyle(sectionElement)
    ;['color', 'fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight', 'textTransform'].forEach(
      (prop) => style[prop as any],
    )

    // Finally, resolve any remaining CSS variables for this section
    resolveAllCssVariables(sectionElement)
  }

  return {
    processPrintContent,
    applyPrintModeToElement,
    resolveAllCssVariables,
    applyPrintModeToSection,
    getNextPrintModeIndex,
    resetPrintModeCounters,
  }
}
